import { useEffect, useState } from 'react'
import {
  Body1,
  Box,
  Button,
  Chip,
  DialogTrigger,
  Divider,
  findBy,
  ListItem,
  ListItemText,
  makeStyles,
  Tooltip,
  toRelativeDateString,
} from '@perk-ui/core'
import { addWeeks, isAfter } from 'date-fns'
import { AlertCircle, ExternalLink, Mail, MessageCircle } from 'react-feather'

import { surveyPeriodsOrdered } from '../config/constants'
import useBodySites from '../features/query-hooks/useBodySites'
import { Patient } from '../features/query-hooks/usePatient'
import { PatientCase } from '../features/query-hooks/usePatientCases'
import useResendSurveyNotification from '../features/query-hooks/useResendSurveyNotification'
import useSurveyCompletionCDStatus, {
  SurveyStatusPeriod,
} from '../features/query-hooks/useSurveyCompletionCDStatus'
import { SurveyResponseGroup } from '../features/query-hooks/useSurveyResponseGroups'
import useDates from '../hooks/useDates'
import { parseYMD } from '../utils/dates'
import { formatPhoneNumber } from './PatientInfo'
import { useCurrentSite } from './SitesProvider'
import SurveyCompletionModal from './SurveyCompletionModal'

export interface PatientOpenSurveyProps {
  openSurvey: SurveyResponseGroup
  patient?: Patient
  patientCase?: PatientCase
  parentToggle?: () => void
}

const useStyles = makeStyles((theme) => ({
  cardTitle: {
    marginBottom: theme.spacing(2),
  },
  alertCircle: {
    color: theme.palette.error.main,
    marginRight: theme.spacing(1),
  },
  listItem: {
    paddingLeft: 0,
    minHeight: 'auto',
    '&&': {
      color: theme.palette.text.primary,
    },
    '&:last-child': {
      // To not impact the containing Card's spacing
      marginBottom: -8,
    },
    '&:hover': {
      textDecoration: 'none',
    },
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(2, 0),
  },
  openSurveyButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    textTransform: 'none',
    borderRadius: '4px',
    padding: theme.spacing(0.5, 1.5),
    fontSize: '14px',
    minHeight: 'auto',
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  resendButton: {
    textTransform: 'none',
    fontSize: '14px',
    padding: theme.spacing(0.5, 1),
    minHeight: 'auto',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  resendEmailButton: {
    color: theme.palette.primary.main,
  },
  resendSmsButton: {
    color: theme.palette.text.secondary,
  },
  surveyChip: {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    fontSize: '12px',
    height: 'auto',
    minHeight: '24px',
    marginBottom: theme.spacing(1),
    maxWidth: '100%',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    '& .MuiChip-label': {
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  },
  bodySiteChip: {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    fontSize: '12px',
    height: 'auto',
    minHeight: '24px',
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    '& .MuiChip-label': {
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  },
  diagnosisChip: {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    fontSize: '12px',
    height: 'auto',
    minHeight: '24px',
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
    '& .MuiChip-label': {
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  },
  treatmentChip: {
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    fontSize: '12px',
    height: 'auto',
    minHeight: '24px',
    marginBottom: theme.spacing(1),
    '& .MuiChip-label': {
      paddingTop: theme.spacing(0.5),
      paddingBottom: theme.spacing(0.5),
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  surveyContainer: {
    marginBottom: theme.spacing(1),
    borderRadius: '8px',
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  resendLink: {
    cursor: 'pointer',
  },
  dialogContent: {
    '& > p': {
      textAlign: 'center',
    },
  },
  dialogButtons: {
    display: 'flex',
    marginTop: theme.spacing(3),
    justifyContent: 'center',
    '& button+button': {
      marginLeft: theme.spacing(5),
    },
  },
  disabledLink: {
    pointerEvents: 'none',
    color: '#919191',
  },
}))

const PatientOpenSurvey: React.FC<PatientOpenSurveyProps> = ({
  openSurvey,
  patient,
  patientCase,
  parentToggle,
}) => {
  const classes = useStyles()
  const { data: bodySites = [] } = useBodySites()
  const { data: surveyCompletionList } = useSurveyCompletionCDStatus(
    patient?.id,
    patientCase?.id,
  )
  const resendSurveyNotification = useResendSurveyNotification()
  const currentSite = useCurrentSite()
  const siteOrganization = currentSite.organization
  const { localizedFormatDate } = useDates()
  const patientPhoneType = patient?.phoneData?.phoneType
    ? `[${patient.phoneData.phoneType}]`
    : ''

  const [surveyModalOpen, setSurveyModalOpen] = useState(false)
  const [activeStep, setActiveStep] = useState(0)
  const diagnosis =
    patientCase?.patientDiagnoses?.[0]?.medicalCondition?.name || null
  const treatment =
    patientCase?.patientTreatments?.[0]?.medicalTreatment?.name || null

  const bodySite = findBy('id', patientCase?.bodySiteId, bodySites)
  const bodySiteName = bodySite?.name || null
  const indexToPeriod = surveyPeriodsOrdered
    .sort((a, b) => a.order - b.order)
    .filter((per) =>
      // per.value !== 0.5 &&
      Object.keys(surveyCompletionList?.surveys || {}).find(
        (surve) => surve === per.type,
      ),
    )
    .map((p) => p.type)

  const renderTitle = () => {
    if (openSurvey.availableOn === null) {
      return `Baseline - expires ${renderExpiresOn(
        parseYMD(openSurvey.expiresOn),
      )}`
    } else {
      return `Followup - expires ${renderExpiresOn(
        parseYMD(openSurvey.expiresOn),
      )}`
    }
  }

  const renderExpiresOn = (date: Date) => {
    const oneWeekForward = addWeeks(Date.now(), 1)
    if (isAfter(date, oneWeekForward)) {
      return `on ${localizedFormatDate(date)}`
    } else {
      return toRelativeDateString(date, Date.now(), 'ceil')
    }
  }

  const handleOpenSurveyModal = () => {
    setSurveyModalOpen(true)
    if (parentToggle) {
      parentToggle()
    }
  }

  const handleCloseSurveyModal = () => {
    setSurveyModalOpen(false)
  }

  const handleContinueInBrowser = () => {
    if (openSurvey && openSurvey.shortLink) {
      const treatmentProductManufacturer =
        patientCase?.patientTreatments?.[0]?.treatmentProduct?.manufacturer ||
        ''

      const surveyUrl =
        openSurvey.shortLink +
        `?comm_channel=manual&org=${siteOrganization.name}&manufacturer=${treatmentProductManufacturer}`

      window.open(surveyUrl, '_blank')
    }
  }

  const handleDirectSurveyAccess = () => {
    if (openSurvey.shortLink) {
      const treatmentProductManufacturer =
        patientCase?.patientTreatments?.[0]?.treatmentProduct?.manufacturer ||
        ''

      const surveyUrl =
        openSurvey.shortLink +
        `?comm_channel=manual&org=${siteOrganization.name}&manufacturer=${treatmentProductManufacturer}`

      window.open(surveyUrl, '_blank')
    }
  }

  const handleSmsClick = (surveyResponseGroupId: string) => {
    return resendSurveyNotification.mutateAsync({
      surveyResponseGroupId: surveyResponseGroupId,
      medium: 'sms',
    })
  }
  const handleEmailClick = (surveyResponseGroupId: string) => {
    return resendSurveyNotification.mutateAsync({
      surveyResponseGroupId: surveyResponseGroupId,
      medium: 'email',
    })
  }
  let isSmsLinkDisabled = false
  let smsDisabledReason = ''
  let isEmailLinkDisabled = false
  let emailDisabledReason = ''
  let openSurveyButtonDisabled = false
  let openSurveyDisabledReason = ''
  if (patient) {
    if (patient.isOptedOutPhone || !patient.isValidPhone) {
      isSmsLinkDisabled = true
      if (patient.isOptedOutPhone && !patient.isValidPhone) {
        smsDisabledReason =
          "Phone number can't receive SMS messages and has also opted-out"
      } else if (!patient.isValidPhone) {
        smsDisabledReason = "Phone number can't receive SMS messages"
      } else if (patient.isOptedOutPhone) {
        smsDisabledReason = 'Phone number has opted-out for SMS messages'
      }
    }
    if (!patient.email) {
      isEmailLinkDisabled = true
      emailDisabledReason = 'No email entered'
    }
  }
  if (!openSurvey.shortLink) {
    openSurveyButtonDisabled = true
    openSurveyDisabledReason = 'Survey link not available'
  }

  useEffect(() => {
    let newActiveStep = 0
    let newActiveStepOrder = 0
    Object.keys(surveyCompletionList?.surveys || {}).map((label) => {
      const period = label as SurveyStatusPeriod
      const indexOrder =
        surveyPeriodsOrdered.find((period) => period.type == label)?.order || 0
      if (
        surveyCompletionList?.surveys[period].status != 'n/a' &&
        indexOrder >= newActiveStepOrder
      ) {
        newActiveStep = indexToPeriod.indexOf(period)
        newActiveStepOrder = indexOrder
      }
    })
    if (activeStep != newActiveStep) {
      setActiveStep(newActiveStep)
    }
  }, [surveyCompletionList?.surveys])

  let currentPeriod = surveyPeriodsOrdered.find(
    (period) => indexToPeriod[activeStep] == period.type,
  )?.slug

  if (
    indexToPeriod &&
    activeStep &&
    indexToPeriod[activeStep].includes('followup')
  ) {
    currentPeriod =
      'Session ' +
      parseInt(indexToPeriod[activeStep].replace('followup', '')) * 5
  }
  Object.keys(surveyCompletionList?.surveys || {})
    .sort()
    .map((sgbs) => {
      if (sgbs.includes('followup')) {
        indexToPeriod.push(sgbs)
      }
    })

  if (!openSurvey.shortLink) {
    openSurveyButtonDisabled = true
    openSurveyDisabledReason = 'Survey link not available'
  }
  return (
    <Box className={classes.surveyContainer}>
      <Box className={classes.chipContainer}>
        {bodySite && (
          <Chip
            label={bodySiteName}
            className={classes.bodySiteChip}
            size="small"
          />
        )}
        {diagnosis && (
          <Chip
            label={diagnosis}
            className={classes.diagnosisChip}
            size="small"
          />
        )}
        {treatment && (
          <Chip
            label={treatment}
            className={classes.treatmentChip}
            size="small"
          />
        )}
      </Box>
      <Divider />
      <Box pr={4} className={classes.row}>
        <DialogTrigger
          disabled={isEmailLinkDisabled}
          action={
            <Tooltip arrow placement="top" title={emailDisabledReason}>
              <span>
                <Button
                  id={'resend-email-link' + openSurvey.id}
                  variant="text"
                  className={`${classes.resendButton} ${classes.resendEmailButton}`}
                  disabled={
                    isEmailLinkDisabled || resendSurveyNotification.isLoading
                  }
                  endIcon={<Mail size={16} />}
                >
                  Resend via Email
                </Button>
              </span>
            </Tooltip>
          }
          onOpen={parentToggle}
          content={(toggle) => (
            <Box p={3} className={classes.dialogContent}>
              <Body1>Are you sure you want to resend this Survey link?</Body1>
              <div className={classes.dialogButtons}>
                <Button
                  id={'resend-email-link' + openSurvey.id + '-cancel-button'}
                  size="medium"
                  variant="outlined"
                  onClick={() => toggle()}
                >
                  Cancel
                </Button>
                <Button
                  id={'resend-email-link' + openSurvey.id + '-confirm-button'}
                  size="medium"
                  onClick={() =>
                    handleEmailClick(openSurvey.id).then(() => toggle())
                  }
                  disabled={resendSurveyNotification.isLoading}
                >
                  Confirm
                </Button>
              </div>
            </Box>
          )}
        />

        <DialogTrigger
          disabled={isSmsLinkDisabled}
          action={
            <Tooltip arrow placement="top" title={smsDisabledReason}>
              <span>
                <Button
                  id={'resend-sms-link' + openSurvey.id}
                  variant="text"
                  className={`${classes.resendButton} ${classes.resendSmsButton}`}
                  disabled={
                    isSmsLinkDisabled || resendSurveyNotification.isLoading
                  }
                  endIcon={<MessageCircle size={16} />}
                >
                  Resend via SMS
                </Button>
              </span>
            </Tooltip>
          }
          onOpen={parentToggle}
          content={(toggle) => (
            <Box p={3} className={classes.dialogContent}>
              <Body1>Are you sure you want to resend this Survey link?</Body1>
              <Body1>
                {patient?.phoneData?.phoneType !== 'MOBILE'
                  ? `${formatPhoneNumber(
                      patient?.cellPhoneLocal || '',
                    )} ${patientPhoneType} may not be able to receive SMS messages. Click Cancel, or click Confirm to send anyway.`
                  : ''}
              </Body1>
              <div className={classes.dialogButtons}>
                <Button
                  id={'resend-sms-link' + openSurvey.id + '-cancel-button'}
                  size="medium"
                  variant="outlined"
                  onClick={() => toggle()}
                >
                  Cancel
                </Button>
                <Button
                  id={'resend-sms-link' + openSurvey.id + '-confirm-button'}
                  size="medium"
                  onClick={() =>
                    handleSmsClick(openSurvey.id).then(() => toggle())
                  }
                  disabled={resendSurveyNotification.isLoading}
                >
                  Confirm
                </Button>
              </div>
            </Box>
          )}
        />

        <Tooltip arrow placement="top" title={openSurveyDisabledReason}>
          <span>
            <Button
              id={'open-patient-survey' + openSurvey.id}
              variant="contained"
              className={classes.openSurveyButton}
              onClick={() =>
                currentSite?.hasQrCode
                  ? handleOpenSurveyModal()
                  : handleDirectSurveyAccess()
              }
              disabled={openSurveyButtonDisabled}
              endIcon={<ExternalLink size={16} />}
            >
              {currentSite?.hasQrCode
                ? 'Open Patient Survey'
                : 'Complete Manually'}
            </Button>
          </span>
        </Tooltip>
      </Box>
      <Divider />
      <ListItem className={classes.listItem}>
        <AlertCircle className={classes.alertCircle} />
        <ListItemText body={renderTitle()} />
      </ListItem>
      <SurveyCompletionModal
        open={surveyModalOpen}
        onClose={handleCloseSurveyModal}
        surveyUrl={
          openSurvey?.shortLink
            ? openSurvey.shortLink +
              `?comm_channel=manual&org=${siteOrganization.name}&manufacturer=${
                patientCase?.patientTreatments?.[0]?.treatmentProduct
                  ?.manufacturer || ''
              }`
            : ''
        }
        surveyContext={`${bodySiteName} - ${currentPeriod || ''}`}
        patientId={patient?.id || ''}
        onContinueInBrowser={handleContinueInBrowser}
      />
    </Box>
  )
}

export default PatientOpenSurvey
